import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { SkeletonCardFields } from 'components/Loading';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Box,
  Grid,
  Tooltip,
  Typography
} from '@material-ui/core';
import { ActionsBuilder } from './components';
import { ScoreBuilder } from './components';
import { constants } from 'views/Campaign';
import { Alert } from '@material-ui/lab';
import { CloseIcon } from 'components';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import { ProductType } from 'constants.js';

const {
  campaignOccurrenceTypes: { INTERNAL_SURVEY_RESPONSE, EXTERNAL_SURVEY_RESPONSE }
} = constants;

const useStyles = makeStyles(theme => ({
  root: {},
  section: {
    marginTop: theme.spacing(2)
  },
  navigation: {
    padding: theme.spacing(2)
  },
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-end'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  }
}));

const Design = props => {
  const { className, staticContext, survey, back, showNav, ...rest } = props;
  const {
    thisSurvey,
    handleSurveyPermissions,
    isLoading,
    isSurveyLoading,
    isTemplateSaved,
    goBack,
    navBackOnError,
    updateCurrentErrorValidationSegment,
    currentContext,
    campaigns,
    saveSurvey,
    closeSurvey,
    isTemplateReadOnly
  } = survey;
  const classes = useStyles();

  useEffect(() => {
    updateCurrentErrorValidationSegment('actions');
    navBackOnError(back);
  }, [thisSurvey, updateCurrentErrorValidationSegment]);

  const isSaveAllowed = () =>
    !(isLoading || isSurveyLoading) &&
    (thisSurvey.id
      ? currentContext.businessContextId === thisSurvey.businessContextId
      : true) &&
    handleSurveyPermissions('Rguest_writeSurvey');

  const filterValidCampaigns = (campaigns, thisSurvey) => {
    return campaigns.filter(campaign => {
      return (
        (campaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE || campaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) &&
        (thisSurvey?.productType === ProductType.List
          ? thisSurvey?.subscriberListId === campaign.subscriberListId
          : campaign.schemaIdentifier === thisSurvey?.schemaIdentifier)
      );
    });
  };

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields />
        </Grid>
      </CardContent>
    );
  };

  return (
    <div className={classes.root}>
      <div>
        <div>
          {isTemplateReadOnly() && (
            <Alert severity="info">
              You can only view this survey and do not have access to edit.
              Tenant Default survey can be edited only with 'ALL' property.
            </Alert>
          )}
        </div>
        <div className={classes.headerContainer}>
          <CloseIcon onClickClose={closeSurvey} />
        </div>
      </div>
      <Card {...rest} className={clsx(classes.root, classes.section, className)}>
        <form autoComplete="off">
            <div className={classes.title}>
              <CardHeader
                subheader="* indicates a required field, actions are optional."
                title="Survey Actions"
                titleTypographyProps={{ variant: 'h1' }}
              />
            </div>
          {(isLoading || isSurveyLoading) && <SkeletonCardContent />}
          {!(isLoading || isSurveyLoading) && (
            <CardContent>
              <ActionsBuilder
                survey={survey}
                campaigns={filterValidCampaigns(campaigns, thisSurvey)}
              />
            </CardContent>
          )}
        </form>
      </Card>
      <Card {...rest} className={clsx(classes.root, classes.section, className)}>
        <form autoComplete="off">
          <div className={classes.title}>
            <CardHeader
              subheader="* indicates a required field, promoter scores are optional."
              title="Survey Post Processing"
              titleTypographyProps={{ variant: 'h1' }}
            />
          </div>
          <Alert severity="info">
            Questions under scores need to be updated when changing the name/title of the questions on the design page.
          </Alert>
          {(isLoading || isSurveyLoading) && <SkeletonCardContent />}
          {!(isLoading || isSurveyLoading) && (
            <CardContent>
              <ScoreBuilder survey={survey} />
            </CardContent>
          )}
        </form>
      </Card>
      <Card {...rest} className={clsx(classes.root, classes.section, className)}>
        {(back || showNav) && (
          <CardActions className={clsx(classes.cardActions, classes.navigation)}>
            <Button
              color="secondary"
              variant="contained"
              disabled={(isLoading || isSurveyLoading)}
              link={back}
              onClick={goBack}>
              Back
            </Button>
            <Typography></Typography>
            <Box flexGrow={1}>&nbsp;</Box>
            <Tooltip
              title={
                !handleSurveyPermissions('Rguest_writeSurvey')
                  ? 'Permission Required'
                  : ''
              }
              arrow
              placement="bottom">
              <span>
                {!isTemplateReadOnly() && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={saveSurvey}
                    disabled={!isSaveAllowed() || isSurveyLoading}>
                    Save Survey
                  </Button>
                )}
              </span>
            </Tooltip>
          </CardActions>
        )}
      </Card>
      {<SaveMessage openSnackbar={isTemplateSaved} />}
    </div>
  );
};

export default Design;
